<!-- Warning — the full text, and when it stops applying.

     Reached from the strip on Now, and from a notification: a warning push
     carries the headline and deep-links straight here, so this screen has to
     stand up as the FIRST thing someone sees rather than assuming Now was.
     Hence its own place line and its own way back. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="warning-detail">
      <div class="sheet" :class="{ active: warning && !expired }">
        <div v-if="warning" class="banner" :class="{ expired }">
          {{ expired ? 'Expired' : severityWord }}
        </div>

        <header class="head">
          <h1>{{ warning ? warning.headline : 'Warning' }}</h1>
          <p class="meta">
            <template v-if="place">{{ place.name }}</template>
            <template v-if="warning">
              <template v-if="place"> · </template>issued {{ stamp(warning.issuedAt) }}
            </template>
          </p>
          <p v-if="warning" class="meta" :class="{ warn: !expired }">
            {{ expired ? `Expired ${stamp(warning.expiresAt)}` : `In force until ${stamp(warning.expiresAt)}` }}
          </p>
        </header>

        <p v-if="warning" class="body">{{ warning.body }}</p>
        <p v-else class="body absent">This warning has expired or been withdrawn.</p>

        <div class="actions">
          <button class="act" type="button" @click="router.push({ name: 'now' })">
            Back to now
          </button>
        </div>

        <p class="source">Sample text. No warnings feed is connected.</p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRouter } from 'vue-router';
import '../chrome.js';
import { place, warningById, warningExpired } from '../weather.js';

// From the route, via `props: true` in the router — so this screen can be
// rendered in a test without standing a router up around it.
const props = defineProps({ id: { type: [String, Number], default: null } });
const router = useRouter();

const warning = computed(() => warningById(props.id));
const expired = computed(() => warningExpired(warning.value));

const SEVERITY = {
  minor: 'Minor', moderate: 'Moderate', severe: 'Severe', extreme: 'Extreme',
};
// Severity is carried by the red banner AND by this word, because a banner is
// a colour and a colour on its own says nothing to half the people reading it.
const severityWord = computed(
  () => `${SEVERITY[warning.value?.severity] ?? 'Weather'} warning`,
);

const stamp = (iso) => (iso
  ? new Date(iso).toLocaleString('en-AU', {
    weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  : '');
</script>

<style scoped>
.warning-detail { --background: var(--oh-paper, #dfe3d1); }
.sheet {
  --paper: var(--oh-paper, #dfe3d1);
  --grid: var(--oh-grid, #b0522f);
  --ink: var(--oh-ink, #1b2540);
  --signal: var(--oh-signal, #a8331a);
  background: var(--paper);
  min-height: 100%;
  padding-top: env(safe-area-inset-top, 0px);
}
.banner {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 14px;
  background: var(--signal);
  color: var(--paper);
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.banner.expired {
  background: transparent;
  color: color-mix(in srgb, var(--ink) 55%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--grid) 30%, transparent);
}
.head { padding: 18px 14px 0; }
h1 {
  margin: 0;
  font-family: var(--oh-serif, Georgia, serif);
  font-style: italic;
  font-weight: 400;
  font-size: 26px;
  line-height: 1.15;
  color: var(--ink);
}
.meta {
  margin: 6px 0 0;
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 11px;
  letter-spacing: 0.06em;
  color: color-mix(in srgb, var(--ink) 60%, transparent);
}
.meta.warn { color: var(--signal); }
.body {
  margin: 0;
  padding: 16px 14px 4px;
  font-family: var(--oh-serif, Georgia, serif);
  font-size: 16px;
  line-height: 1.5;
  color: var(--ink);
}
.body.absent { color: color-mix(in srgb, var(--ink) 60%, transparent); }
.actions { display: flex; padding: 18px 14px; }
.act {
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid color-mix(in srgb, var(--ink) 35%, transparent);
  border-radius: 2px;
  background: transparent;
  color: var(--ink);
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 12px;
  letter-spacing: 0.06em;
  cursor: pointer;
}
.act:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
.source {
  margin: 0;
  padding: 0 14px calc(20px + env(safe-area-inset-bottom, 0px));
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 10px;
  letter-spacing: 0.05em;
  color: color-mix(in srgb, var(--ink) 45%, transparent);
}
</style>
